import { Calendar, AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { ExtractedDate } from '@/types/extraction'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { daysUntilDeadline, extractedDeadlineToApply } from '@/lib/bid-deadline'

/**
 * A pending addendum-proposed deadline change surfaced from a review-status
 * reconciliation run. The card renders an amber warnstrip linking to the
 * review surface; it clears automatically once the proposal is decided (the
 * server stops carrying `highlights` on a settled run).
 */
export interface DeadlinePendingChange {
  /** Human label for the source (e.g. addendum title); falls back to 'An addendum'. */
  label: string | null
  /** A concrete proposed date — the warnstrip only surfaces when one exists. */
  proposedDateIso: string
  runId: string
}

function formatDate(dateIso: string | null, dateText: string): string {
  if (!dateIso) return dateText
  try {
    return new Date(dateIso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateText
  }
}

type DeadlineStatus = {
  /** Tailwind classes for the tinted surface (12% hue bg) + full-hue text/icon. */
  classes: string
  label: string
}

/**
 * Maps days-until-deadline to a status color + label.
 * - red (destructive): past due, due today, or within 2 days (urgent)
 * - amber (status-warning): approaching, within 14 days
 * - green (status-success): comfortably in the future
 */
function getDeadlineStatus(daysUntil: number | null): DeadlineStatus | null {
  if (daysUntil == null) return null

  const label =
    daysUntil < 0
      ? 'Past due'
      : daysUntil === 0
        ? 'Due today'
        : `${daysUntil} day${daysUntil === 1 ? '' : 's'}`

  if (daysUntil <= 2) {
    return { label, classes: 'bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]' }
  }
  if (daysUntil <= 14) {
    return { label, classes: 'bg-[hsl(var(--status-warning)/0.12)] text-[hsl(var(--status-warning))]' }
  }
  return { label, classes: 'bg-[hsl(var(--status-success)/0.12)] text-[hsl(var(--status-success))]' }
}

export function SubmissionDeadlineCard({
  deadline,
  bidId,
  pendingChange,
  bidDeadline,
  onSetBidDeadline,
}: {
  deadline: ExtractedDate
  bidId: string
  pendingChange?: DeadlinePendingChange | null
  /** The bid record's own deadline, for reconciliation against the extracted one. */
  bidDeadline?: string | null
  /** Omit to render read-only (no apply affordance). */
  onSetBidDeadline?: ((dateIso: string) => void) | undefined
}) {
  const daysUntil = daysUntilDeadline(deadline.dateIso)
  const status = getDeadlineStatus(daysUntil)
  const showWarnstrip = Boolean(pendingChange)

  // The extracted closing date does not reach the bid record on its own, so
  // offer to apply it right where the contradiction is visible — the metadata
  // strip directly above this card is what reads "Due No deadline" (F39).
  const applicable = onSetBidDeadline
    ? extractedDeadlineToApply(deadline.dateIso, bidDeadline)
    : null

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-4 p-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
              status ? status.classes : 'bg-[hsl(var(--brand)/.1)] text-[hsl(var(--brand))]',
            )}
          >
            <Calendar className="h-[18px] w-[18px]" />
          </div>
          <div>
            <p className="text-sm font-medium">{deadline.event}</p>
            <p className="text-[13px] text-muted-foreground">
              {formatDate(deadline.dateIso, deadline.dateText)}
              {deadline.time ? ` · ${deadline.time}` : ''}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {applicable && onSetBidDeadline && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => onSetBidDeadline(applicable.toISOString())}
            >
              Set as bid deadline
            </Button>
          )}
          {status && (
            <span className={cn('rounded-md px-2.5 py-1 text-xs font-medium', status.classes)}>
              {status.label}
            </span>
          )}
        </div>
      </div>
      {showWarnstrip && pendingChange && (
        <div className="mx-4 mb-4 flex items-start gap-2 rounded-lg border border-[hsl(var(--status-warning)/0.2)] bg-[hsl(var(--status-warning)/0.12)] px-3 py-2 text-xs text-[hsl(var(--status-warning))]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {pendingChange.label ?? 'An addendum'} proposes{' '}
            {formatDate(pendingChange.proposedDateIso, pendingChange.proposedDateIso)} —{' '}
            <Link
              to={`/app/bids/${bidId}/qualify?view=addendum-review&run=${pendingChange.runId}`}
              className="font-medium underline underline-offset-2"
            >
              Review
            </Link>
          </span>
        </div>
      )}
    </div>
  )
}
